import { hasUnusualQuantity } from './PrescribedItemList/utils/hasUnusualQuantity'
import { REQUIRE_PHONE_AUTH_RESTRICTION_TYPES } from '../../constants'
import { PrescribedItem } from '../../types'
import { PbsProgram, ProductDetails, StateSchedule } from './ScriptForm/ProductSearchContextSlice'
import { get } from 'lodash'

export const isAuthPhoneRequiredForPrescribedItem = (
  prescribedItem: PrescribedItem | undefined
) => {
  if (!prescribedItem) {
    return false
  }

  const pbsArray = prescribedItem?.productDetails?.pbs || []
  const selectedPBS = pbsArray.find(({ pbsCode }) => pbsCode === prescribedItem?.pbsCode)

  if (!selectedPBS) {
    return false
  }

  return (
    hasUnusualQuantity(prescribedItem) ||
    REQUIRE_PHONE_AUTH_RESTRICTION_TYPES.includes(selectedPBS?.restrictions[0]?.restrictionType)
  )
}

export const isSchedule8Medicine = (prescribedItem: PrescribedItem) => {
  if (prescribedItem.isExtemp) {
    return !!prescribedItem.isSchedule8
  }
  return !!prescribedItem?.productDetails?.poisonClassification?.poisonClasses.includes('S8')
}

interface Map {
  [key: string]: string | undefined
}
export const RESTRICTION_TYPE_MAPPING: Map = {
  'online authority required': 'Phone/Online authority required',
}

export const convertRestrictionType = (restrictionType: string) => {
  const mappingResult = RESTRICTION_TYPE_MAPPING[restrictionType.toLowerCase()]

  return mappingResult ?? restrictionType ?? ''
}

export function repeatIntervalRequired(
  numOfRepeats: number | string,
  state: string | undefined,
  drugDetails: ProductDetails | undefined
): boolean {
  if (Number(numOfRepeats) === 0 || !state || !drugDetails) {
    return false
  }

  const poisonClasses: string[] = get(drugDetails, 'poisonClassification.poisonClasses', [])
  const stateSchedules: (StateSchedule | null)[] = get(drugDetails, 'stateSchedules', [])

  if (poisonClasses.includes('S8')) {
    return true
  }

  if (state === 'NSW') {
    return !!stateSchedules.find((ss) => ss?.state === 'NSW' && ss.schedules.includes('S4B'))
  }

  if (state === 'TAS') {
    return !!stateSchedules.find((ss) => ss?.state === 'TAS' && ss.schedules.includes('S4D'))
  }

  if (state === 'VIC') {
    return !!stateSchedules.find((ss) => ss?.state === 'VIC' && ss.schedules.includes('S11'))
  }

  return false
}

export const describePbsProgram = (pbsProgram?: PbsProgram) => {
  switch (pbsProgram?.programCode) {
    case 'CA':
      return 'Community Access'
    case 'HS':
      return 'Private Hospitals'
    case 'HB':
      return 'Public Hospitals'
    case 'GE':
      return 'General Schedule'
    default:
      return ''
  }
}

export function numberToWords(num: number | string | null | undefined): string | undefined {
  if (num === undefined || num === null || num === '') return undefined
  if (typeof num === 'string') num = parseInt(num, 10)
  if (isNaN(num)) return undefined
  if (num === 0) return 'zero'

  const belowTwenty = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ]
  const tens = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ]
  const thousands = ['', 'thousand', 'million', 'billion', 'trillion']

  function convertChunk(n: number): string {
    if (n === 0) return ''
    else if (n < 20) return belowTwenty[n] + ' '
    else if (n < 100) return tens[Math.floor(n / 10)] + ' ' + convertChunk(n % 10)
    else return belowTwenty[Math.floor(n / 100)] + ' hundred ' + convertChunk(n % 100)
  }

  let result = ''
  let i = 0

  while (num > 0) {
    const chunk = num % 1000
    if (chunk !== 0) {
      result = convertChunk(chunk) + thousands[i] + ' ' + result
    }
    num = Math.floor(num / 1000)
    i++
  }

  return result.trim()
}
